/**
 * Stack - Vertical Layout Primitive
 *
 * (Renamed from FixedLayout for industry-standard naming)
 *
 * Provides vertical stacking with optional header/footer:
 * - Fixed header at the top
 * - Scrollable content in the middle
 * - Fixed footer at the bottom
 *
 * Works seamlessly in both standalone and bundle modes.
 *
 * @example
 * ```tsx
 * <Stack
 *   header={<ChatHeader />}
 *   footer={<MessageComposer />}
 * >
 *   <MessagesList />
 * </Stack>
 * ```
 */

import { ReactNode } from 'react';
import './Stack.css';

export interface StackProps {
  /**
   * Header content (optional)
   * If provided, will be fixed at the top
   */
  header?: ReactNode;

  /**
   * Footer content (optional)
   * If provided, will be fixed at the bottom
   */
  footer?: ReactNode;

  /**
   * Main content area (scrollable)
   */
  children: ReactNode;

  /**
   * Additional CSS class for the container
   */
  className?: string;

  /**
   * Additional CSS class for the content area
   */
  contentClassName?: string;

  /**
   * Allow content area to scroll internally
   * @default true
   */
  scrollable?: boolean;
}

/**
 * Stack component - vertical layout primitive
 */
export function Stack({
  header,
  footer,
  children,
  className = '',
  contentClassName = '',
  scrollable = true,
}: StackProps) {
  const contentClasses = [
    'stack__content',
    scrollable ? 'stack__content--scrollable' : 'stack__content--static',
    contentClassName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`stack stack-fill ${className}`.trim()}>
      {header && <div className="stack__header">{header}</div>}
      <div className={contentClasses}>{children}</div>
      {footer && <div className="stack__footer">{footer}</div>}
    </div>
  );
}

export default Stack;
